import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import './RunCard.css';
import './response/ResponsePattern.css';

/**
 * RunCard — the AI assistant's working state.
 *
 * Implements the "Working State" design canvas artboard. The premise there:
 * a 5–30 second run needs reassurance, not a transcript. So the card shows one
 * plain-language line for what is happening now, a four-step spine so the end
 * is in sight, and elapsed time. Deliberately absent:
 *
 *   - tool names — extra tool calls collapse into the phase they belong to,
 *     so a chatty run still reads as four steps
 *   - model reasoning — the server no longer sends it at all (see the
 *     onThinking comment in fastify/routes/ai/queryHotelData.js)
 *   - anything that changes faster than a person can read — the activity line
 *     is rate-limited to ACTIVITY_MIN_MS
 *
 * The artboard's "Taking longer than usual" state is intentionally not built:
 * per Derek, a run that is slow should not spend the user's attention telling
 * them so.
 *
 * On completion the card collapses to the design's one-line run summary
 * ("✓ 4 steps · 3 sources · 3m 54s · trace"); the trace disclosure behind it
 * lists each call in mono, one row per tool, for readers who want provenance.
 */

/** The activity line never changes faster than this — see "nothing that changes faster than a person can read". */
const ACTIVITY_MIN_MS = 700;

/**
 * The four-step spine. Every tool call the agent makes lands in one of these,
 * so the spine length is fixed no matter how chatty the run is.
 */
const PHASES = [
  { key: 'read', label: 'Your question', activity: 'Reading your question', sub: 'Working out what to check first' },
  { key: 'gather', label: 'Gathering data', activity: 'Gathering data', sub: 'Pulling the figures this needs' },
  { key: 'check', label: 'Checking', activity: 'Checking the numbers', sub: 'Making sure the figures line up' },
  { key: 'answer', label: 'Your answer', activity: 'Writing the answer', sub: 'Putting the findings together' },
];

/** Backend status strings that mean "past data gathering, now verifying". */
const CHECKING_STATUS = /verify|verifying|checking the answer|preparing|building/i;
/** Backend status strings that mean "composing the reply". */
const ANSWERING_STATUS = /generating response|wrapping up|writing/i;

/**
 * Which of the four phases a run is in, from signals the client already has:
 * the tool steps, the backend's plain-language status line, and whether answer
 * tokens have started flowing.
 */
export function derivePhase({ steps = [], status = '', writing = false }) {
  if (writing || ANSWERING_STATUS.test(status)) return 3;
  const tools = steps.filter(s => s.kind === 'tool');
  const running = tools.filter(s => s.state === 'running').length;
  const done = tools.filter(s => s.state === 'done' || s.state === 'error').length;
  if (CHECKING_STATUS.test(status)) return 2;
  if (done > 0 && running === 0) return 2;
  if (tools.length > 0) return 1;
  return 0;
}

/**
 * Step labels are live-status gerunds ("Checking mapping health") because that
 * is what they say while a run is in flight. Under the finished card's "What I
 * did" heading they need the past tense. Only the leading verb changes.
 */
const PAST_TENSE = {
  Analyzing: 'Analyzed', Building: 'Built', Calculating: 'Calculated',
  Checking: 'Checked', Comparing: 'Compared', Computing: 'Computed',
  Generating: 'Generated', Inspecting: 'Inspected', Loading: 'Loaded',
  Preparing: 'Prepared', Querying: 'Queried', Reading: 'Read',
  Running: 'Ran', Saving: 'Saved', Scanning: 'Scanned',
  Searching: 'Searched', Shopping: 'Shopped', Updating: 'Updated',
  Verifying: 'Verified', Writing: 'Wrote',
};

export function toPastTense(label) {
  const text = String(label || '').trim();
  const verb = text.split(' ')[0];
  return PAST_TENSE[verb] ? `${PAST_TENSE[verb]}${text.slice(verb.length)}` : text;
}

/**
 * Plain-language recap lines for a finished run. Built from the per-step
 * labels the server attaches (registry statusText), de-duplicated and counted —
 * never tool names, never arguments.
 */
export function buildRecap(steps = []) {
  const tools = steps.filter(s => s.kind === 'tool');
  if (!tools.length) return [];
  const counts = new Map();
  for (const s of tools) {
    const label = (s.label || '').trim();
    if (!label) continue;
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  const lines = Array.from(counts.entries())
    .map(([label, n]) => (n > 1 ? `${toPastTense(label)} (${n}×)` : toPastTense(label)));
  const failed = tools.filter(s => s.state === 'error').length;
  if (failed) lines.push(`${failed} check${failed === 1 ? '' : 's'} could not complete`);
  return lines;
}

function formatElapsed(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}

/**
 * Rate-limited value: returns `value`, but never updates more often than
 * `minMs`. Keeps the activity line readable on fast, chatty runs.
 */
function useSettled(value, minMs) {
  const [settled, setSettled] = useState(value);
  const lastRef = useRef(0);
  const pendingRef = useRef(null);

  useEffect(() => {
    if (value === settled) return undefined;
    const since = Date.now() - lastRef.current;
    if (since >= minMs) {
      lastRef.current = Date.now();
      setSettled(value);
      return undefined;
    }
    pendingRef.current = value;
    const t = setTimeout(() => {
      lastRef.current = Date.now();
      setSettled(pendingRef.current);
    }, minMs - since);
    return () => clearTimeout(t);
  }, [value, settled, minMs]);

  return settled;
}

/** One row of the four-step spine. */
function StepRow({ phase, state, time }) {
  const done = state === 'done';
  const active = state === 'active';
  return (
    <div className="rc-step">
      <span className={`rc-step-mark${done ? ' is-done' : ''}${active ? ' is-active' : ''}`}>
        {done && <Check size={9} strokeWidth={3.5} />}
      </span>
      <span className={`rc-step-label${active ? ' is-active' : ''}${done ? ' is-done' : ''}`}>{phase.label}</span>
      <span className="rc-step-time">{time || ''}</span>
    </div>
  );
}

/**
 * @param {Object[]} steps      trace steps for this turn (tool steps only are used)
 * @param {string}   status     the backend's current plain-language status line
 * @param {boolean}  writing    answer tokens are streaming
 * @param {number}   startedAt  epoch ms the turn began
 * @param {Function} onStop     abort the turn
 * @param {string}   scopeLabel optional scope context, e.g. "10 properties"
 * @param {boolean}  done       render the finished pill instead of the live card
 * @param {number}   durationMs finished-run duration
 * @param {string}   summary    finished-run one-liner
 */
export default function RunCard({
  steps = [],
  status = '',
  writing = false,
  startedAt = null,
  onStop = null,
  scopeLabel = null,
  done = false,
  durationMs = 0,
  summary = null,
  defaultRecapOpen = false,
}) {
  const [now, setNow] = useState(() => Date.now());
  const [recapOpen, setRecapOpen] = useState(defaultRecapOpen);

  // One tick per second — the elapsed readout is the only thing that needs it.
  useEffect(() => {
    if (done || !startedAt) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [done, startedAt]);

  const phaseIndex = derivePhase({ steps, status, writing });
  const settledPhase = useSettled(phaseIndex, ACTIVITY_MIN_MS);

  // The activity line prefers the server's own words when it has any — they
  // are more specific than the phase default ("Loading financial data" beats
  // "Gathering data") — and is rate-limited so it stays readable.
  const rawActivity = (status || '').replace(/\.{3}$|…$/, '').trim() || PHASES[phaseIndex].activity;
  const activity = useSettled(rawActivity, ACTIVITY_MIN_MS);

  if (done) {
    // Finished turn — the design's run line: "✓ 4 steps · 3 sources · 3m 54s ·
    // trace". Steps are the tool calls, sources the distinct steps that
    // returned data. Neither the line nor the trace disclosure behind it
    // names a tool: the trace lists each step's plain-language label
    // ("Loaded revenue trend"), which is the provenance a reader needs —
    // tool names stay server-side (tests/noReasoningToClient.test.js).
    const tools = steps.filter(s => s.kind === 'tool');
    const stepCount = tools.length;
    const sourceCount = new Set(tools.filter(s => s.state !== 'error').map(s => s.label).filter(Boolean)).size;
    const parts = [];
    if (stepCount) parts.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
    if (sourceCount) parts.push(`${sourceCount} source${sourceCount === 1 ? '' : 's'}`);
    if (durationMs > 0) parts.push(formatElapsed(durationMs));
    if (!parts.length && summary) parts.push(summary);
    const traceRows = tools.map((s, i) => {
      const bits = [];
      if (s.args) bits.push(String(s.args).replace(/\s+/g, ' ').trim());
      else if (s.subtitle) bits.push(String(s.subtitle).trim());
      if (s.rows != null) bits.push(`${Number(s.rows).toLocaleString()} ${s.rowsLabel || 'rows'}`);
      if (s.state === 'error') bits.push('failed');
      return { key: s.id ?? i, step: toPastTense(s.label || 'step'), meta: bits.join(' · '), error: s.state === 'error' };
    });
    return (
      <div className="rc-done">
        <div className="rp-run">
          <Check size={13} strokeWidth={2.4} className="rp-run-check" />
          <span className="rp-run-text">{parts.join(' · ') || 'Answered'}</span>
          {traceRows.length > 0 && (
            <button
              type="button"
              className="rp-run-toggle"
              onClick={() => setRecapOpen(o => !o)}
              aria-expanded={recapOpen}
            >trace</button>
          )}
        </div>
        {recapOpen && traceRows.length > 0 && (
          <div className="rp-trace" style={{ marginTop: 10 }}>
            {traceRows.map(row => (
              <div className={`rp-trace-row${row.error ? ' is-error' : ''}`} key={row.key} title={`${row.step}${row.meta ? ` · ${row.meta}` : ''}`}>
                <span className="rp-trace-tool">{row.step}</span>
                {row.meta && <span className="rp-trace-meta">· {row.meta}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const elapsed = startedAt ? formatElapsed(now - startedAt) : null;
  // Before the request is even sent the dock is waiting on the attachment
  // review (AIChat holds the message until every upload settles). Saying
  // "working out what to check" there reads as the model racing ahead of
  // the file; say what is actually happening.
  const reviewingFiles = /^reviewing\s+\d+\s+attached file/i.test(status || '');
  const sub = reviewingFiles
    ? 'Reading and transcribing the file first — the question is sent once that finishes'
    : settledPhase === 1 && scopeLabel ? scopeLabel : PHASES[settledPhase].sub;

  return (
    <div className="rc-card">
      <div className="rc-head">
        <span className="ai-stream-orb rc-orb">
          <span className="ai-stream-orb-ring" />
          <span className="ai-stream-orb-core" />
        </span>
        <div className="rc-head-text">
          <div className="rc-activity">{activity}</div>
          <div className="rc-activity-sub">{sub}</div>
        </div>
        {elapsed && <span className="rc-elapsed">{elapsed}</span>}
        {onStop && (
          <button type="button" className="rc-stop" onClick={onStop}>
            Stop
          </button>
        )}
      </div>

      <div className="rc-bar">
        <div className="rc-bar-fill" />
      </div>

      <div className="rc-steps">
        {PHASES.map((phase, i) => (
          <StepRow
            key={phase.key}
            phase={phase}
            state={i < settledPhase ? 'done' : i === settledPhase ? 'active' : 'queued'}
            time={i < settledPhase && startedAt ? '' : ''}
          />
        ))}
      </div>
    </div>
  );
}
